import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../api/services';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

/**
 * Senior Level Utility: Decode JWT without external libraries
 */
function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
}

function readCachedUser() {
  const raw = localStorage.getItem('user');
  if (!raw) return null;
  try {
    const u = JSON.parse(raw);
    return u && typeof u === 'object' ? u : null;
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Automatic logout timer reference
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !user) return undefined;

    const decoded = parseJwt(token);
    if (!decoded || !decoded.exp) return undefined;

    // Convert exp (seconds) to milliseconds
    const expirationTime = decoded.exp * 1000;
    const currentTime = Date.now();
    const timeLeft = expirationTime - currentTime;

    if (timeLeft <= 0) {
      logout();
      return undefined;
    }

    // Set timer to log out automatically when token expires
    const logoutTimer = setTimeout(() => {
      console.log('Session expired. Logging out automatically...');
      logout();
    }, timeLeft);

    return () => clearTimeout(logoutTimer);
  }, [user]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await authAPI.getUserByToken(token);
        if (cancelled) return;
        if (res.data?.data) {
          setUser(res.data.data);
          localStorage.setItem('user', JSON.stringify(res.data.data));
        } else {
          const cached = readCachedUser();
          if (cached) setUser(cached);
          else {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }
        }
      } catch (err) {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        } else {
          const cached = readCachedUser();
          if (cached) setUser(cached);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (phone, password) => {
    const res = await authAPI.signIn({ phone, password });

    const token = res.data?.meta?.token;
    const userData = res.data?.data;

    if (!token) {
      throw new Error('No token received from server');
    }

    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);

    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    if (window.location.pathname.startsWith('/admin')) {
      window.location.href = '/login';
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};
